import React from 'react';
import { Virtuoso } from 'react-virtuoso';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { Modal } from '@components/main-components/modals/Modal';
import { RowSkeleton } from '@components/modals/PlaylistReleases/ReleaseRowSkeleton';
import { ModalContext } from '@context/modal';
import { PlaylistReleasesModalDocument, useInfiniteQuery } from '@graphql/reactQuery';

import { ScrollArea } from '../shared/styles';
import { ReleaseRow } from './ReleaseRow';

/*#__PURE__*/ gql(/* GraphQL */ `
  query PlaylistReleasesModal(
    $playlistId: UUID!
    $id: UUID!
    $pagination: PlaylistActionReleasesCursorConnectionArgs!
    # These help us optimize to only get back added or removed releases depending on the type of action
    # Although backend always serve one of these but still
    $isAdded: Boolean!
    $isRemoved: Boolean!
  ) {
    playlistAction(playlistId: $playlistId, id: $id) {
      __typename
      id

      ... on ReleasesAddedToPlaylist {
        id
        addedReleases(pagination: $pagination) @include(if: $isAdded) {
          edges {
            node {
              id
              createdAt
              ...ReleaseRowList
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }

      ... on ReleasesRemovedFromPlaylist {
        id
        removedReleases(pagination: $pagination) @include(if: $isRemoved) {
          edges {
            node {
              id
              createdAt
              ...ReleaseRowList
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }

      ... on ReleasesAddedRemovedFromPlaylist {
        id
        addedReleases(pagination: $pagination) @include(if: $isAdded) {
          edges {
            node {
              id
              createdAt

              ...ReleaseRowList
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
        removedReleases(pagination: $pagination) @include(if: $isRemoved) {
          edges {
            node {
              id
              createdAt
              ...ReleaseRowList
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
`);

export function PlaylistReleasesModal({
  playlistId,
  actionId,
  type,
}: {
  playlistId: string;
  actionId: string;
  type: 'added' | 'removed';
}) {
  const { closeCancelModal } = ModalContext.useContainer();

  const {
    orderedList: actions,
    isFetching,
    isRefetching,
    loadMoreNextPage,
  } = useInfiniteQuery(PlaylistReleasesModalDocument, {
    getNextPageParam({ data: { playlistAction } }) {
      switch (playlistAction?.__typename) {
        case 'ReleasesAddedToPlaylist': {
          return (
            playlistAction.addedReleases?.pageInfo.hasNextPage && {
              after: playlistAction.addedReleases?.pageInfo.endCursor,
            }
          );
        }
        case 'ReleasesRemovedFromPlaylist': {
          return (
            playlistAction.removedReleases?.pageInfo.hasNextPage && {
              after: playlistAction.removedReleases?.pageInfo.endCursor,
            }
          );
        }
        case 'ReleasesAddedRemovedFromPlaylist': {
          return type === 'added'
            ? playlistAction.addedReleases?.pageInfo.hasNextPage && {
                after: playlistAction.addedReleases.pageInfo.endCursor,
              }
            : playlistAction.removedReleases?.pageInfo.hasNextPage && {
                after: playlistAction.removedReleases?.pageInfo.endCursor,
              };
        }
        default: {
          return null;
        }
      }
    },
    filterQueryKey: {
      actionId,
      playlistId,
      type,
    },
    variables({ pageParam }) {
      return {
        id: actionId,
        playlistId,
        isAdded: type === 'added',
        isRemoved: type === 'removed',
        pagination: {
          first: 10,
          after: pageParam?.after ?? null,
        },
      };
    },
    list(result) {
      switch (result.playlistAction?.__typename) {
        case 'ReleasesAddedToPlaylist': {
          return result.playlistAction.addedReleases?.edges.map(v => v.node);
        }
        case 'ReleasesRemovedFromPlaylist': {
          return result.playlistAction.removedReleases?.edges.map(v => v.node);
        }
        case 'ReleasesAddedRemovedFromPlaylist': {
          return type === 'added'
            ? result.playlistAction.addedReleases?.edges.map(v => v.node)
            : result.playlistAction.removedReleases?.edges.map(v => v.node);
        }

        default: {
          return [];
        }
      }
    },
    order: [v => v.createdAt, 'asc'],
    uniq({ id }) {
      return id;
    },
    staleTime: 0,
  });

  const [scrollParent, setScrollParent] = React.useState<HTMLDivElement | undefined>();

  const itemContent = React.useCallback(
    (_: unknown, releaseFrag: (typeof actions)[number]) => (
      <div key={releaseFrag.id}>
        <ReleaseRow releaseFrag={releaseFrag} playlistId={playlistId} displayDropdownAsModal />
      </div>
    ),
    [playlistId],
  );

  const showShouldFooter = isFetching && !isRefetching;

  const Footer = React.useCallback(() => {
    return (
      <StyledFooter>
        {showShouldFooter && (
          <ReleasesContainer>
            <RowSkeleton count={5} />
          </ReleasesContainer>
        )}
      </StyledFooter>
    );
  }, [showShouldFooter]);

  const body = (
    <>
      <ScrollAreaReleases
        ref={r => {
          setScrollParent(r || undefined);
        }}
        css={{
          overflow: actions && actions.length <= 5 ? 'hidden' : 'auto',
        }}
      >
        <Virtuoso
          data={actions}
          itemContent={itemContent}
          customScrollParent={scrollParent}
          endReached={loadMoreNextPage}
          components={{ Footer }}
        />
      </ScrollAreaReleases>
    </>
  );

  return (
    <Modal
      headerText={`Sounds ${type}`}
      body={body}
      closeModal={closeCancelModal}
      bodyStyles={{
        marginTop: 18,
      }}
      containerStyles={{
        maxWidth: 420,
        padding: '16px 0px 0px 8px',

        '@tablet': {
          padding: '24px 0px 0px 16px',
        },
      }}
      headerStyles={{
        alignItems: 'center',
        textTransform: 'capitalize',
        paddingRight: 16,
        paddingLeft: 8,

        '@tablet': {
          paddingRight: 24,
        },
      }}
    />
  );
}

const ReleasesContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
});

const StyledFooter = styled('div', {
  height: 1,

  '@tabletLandscape': {
    minHeight: 10,
    height: 'fit-content',
  },
});

const ScrollAreaReleases = styled(ScrollArea, {
  height: 330,
  maxHeight: 330,
  marginTop: 0,
});
